/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

// Descriptor

import {EventEmitter} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';

import {FormCardComponent} from './form-card/form-card.component';
import {FormTableComponent} from './form-table/form-table.component';

import {EmpFile} from '../employees/employees.model';

export interface FormDescriptor {
  tabSheets: Array<TabSheetDescriptor>;
  localeString: string;
  currencyString: string;
  cliRegID: number;
}

export enum EmpZoneDependencyAction {
  FILTER = 'FILTER',
  SET_DISABLED = 'SET_DISABLED',
  SET_REQUIRED = 'SET_REQUIRED',
  SET_CALCULATION_DISABLED = 'SET_CALCULATION_DISABLED',
}

export interface EmpZoneDependency {
  source: string;
  trigger: string;
  target: string;
  action: EmpZoneDependencyAction;
  actionValue: string;
}

export type TabType = 'EMP_TAB' | 'EMP_SKILL_TAB' | 'CONTRACT_TAB';

export interface TabSheetDescriptor {
  tabSheetID: string;
  translate?: string;
  tabSheetPosition: number;
  tabType?: string;
  groupZones: Array<GroupZoneDescriptor>;
  transId: string;
  active?: boolean;
  error?: boolean;
  type?: TabType;
}

export interface GroupZoneDescriptor {
  groupZoneID: string;
  groupZoneType: GroupZoneType;
  groupZoneKeyName?: string;
  tabSheetPosition?: number;
  transId?: string;
  translate?: string;
  metadatas: any;
  displayorder: number;
  internalType: string;
  nbCol: number;
  isDated: boolean;
  zones: Array<ZoneDescriptor>;
  isFocused?: boolean;
  switchEditMode?: EventEmitter<boolean>;
  isPMTransfer?: boolean;
  displayType?: string;
  readOnly: boolean;
  card?: FormCardComponent;
  table?: FormTableComponent;
  zoneDescriptors?: { [key: string]: ZoneDescriptor };
  dependencies?: Array<EmpZoneDependency>;
}

export interface ZoneDescriptor {
  zoneID: string;
  transId: string;
  isRequired: boolean;
  _isRequired?: boolean;
  defaultValue?: any;
  displayorder: number;
  metadatas: any;
  nbColumns: number;
  nbRows: number;
  width?: number;
  height?: number;
  internalType: string;
  alignment?: string;
  type: string;
  _type?: string;
  isFocused?: boolean;
  zoneGroup?: string;
  hidden: boolean;
  readOnly: boolean;
  _readOnly?: boolean;
  isReadOnly?: boolean;
  regExp?: string;
  regExpMessage?: string;
  repInheritedValue?: string;
  isKeyZone?: boolean;
  validation?: string;
  nbDigitsAfterDecimal?: number;
  inputMask?: string;
  arg1?: string;
  arg2?: string;
  calculation?: {
    calculationType?: string;
    dependencies?: string[];
  }
}

export interface ControlAndZoneDescriptor {
  control: UntypedFormControl;
  descriptor: ZoneDescriptor;
}

export interface Field {
  control: UntypedFormControl;
  descriptor: ZoneDescriptor;
  value: Value;

  initialize?();
}

// Values

export interface Value {
  value: any;
  type: string;
  transId?: string;
  file?: EmpFile;
  cleared?: boolean;
}

// Store this when a field is focused or blurred
export interface FieldEventTrace {
  value: any;
  descriptor: ZoneDescriptor;
  dataIndex?: number;
}

// Zone values : values for a groupZone

export interface ZoneValues {
  id?: string;
  dataIndex?: number;
  groupName: string;
  _groupName?: string;  // for banking details
  isDated: boolean;
  contractId?: number;
  empContractId?: number;
  groupDescriptor?: GroupZoneDescriptor;
  begindate: string;
  enddate: string;
  valid?: boolean;
  values: { [key: string]: Value };
  _values?: { [key: string]: Value }; // for banking details
  default?: boolean;
  validRangeLabel?: string;
  cleared?: boolean;
}

export type ZoneValuesByGroup = Record<string, ZoneValues[]>;

export type SimulationContractZoneValue = Pick<
  ZoneValues,
  'begindate' | 'default' | 'enddate' | 'groupName' | 'id' | 'isDated'
> & {
  values?: ZoneValues['values'];
};

export type SimulationContractData = Record<string, SimulationContractZoneValue[]>;

export type SimulationResponseByTab = Record<string, ZoneValuesByGroup>;

export interface SimulationPatchCard {
  descriptor?: { groupZoneID?: string };
  activeEntity?: { begindate?: string; enddate?: string };
}

// Form values : all zone values for a form

export type FormValues = Map<string, Array<ZoneValues>>;

export interface TabSheetAndControlGroup {
  tabSheet: TabSheetDescriptor;
  controlGroup: UntypedFormGroup;
}

export interface FormValuesWrapper {
  data: any;
  descriptors: Map<string | number, GroupZoneDescriptor>;
  zonesValues: Map<string | number, Array<ZoneValues>>;
}

export enum GroupZoneType {
  UNIQUE_MANDATORY = 'UNIQUE_MANDATORY',
  MULTIPLE_MANDATORY = 'MULTIPLE_MANDATORY',
  UNIQUE_NON_MANDATORY = 'UNIQUE_NON_MANDATORY',
  MULTIPLE_NON_MANDATORY = 'MULTIPLE_NON_MANDATORY',
}

// nb: key is BE name, value is historical FE name (used for translation)
export enum FormGeneratorErrorKind {
  // GROUPS
  BEGIN_AFTER_END = 'dated-block-add-begin-after-end',
  ONE_LINE_REQUIRED = 'at-least-one-line-required',
  ONLY_ONE_LINE_ALLOWED = 'only-one-line-allowed',
  ONLY_ONE_LINE_ALLOWED_BY_KEY = 'only-one-line-allowed-by-key',

// nb: BE has just KEY_REQUIRED
  KEY_REQUIRED = 'key-required',
  KEYS_REQUIRED = 'keys-are-required',

  // nb: BE has just GAPS
  GAPS = 'dated-block-gaps',
  GAPS_WITH_KEY = 'dated-block-gaps-withkey',

  // nb: BE has just BEGIN_DATE_ETERNITY and END_DATE_ETERNITY
  DATED_BLOCK_FIRST_OCCURENCE_BEGIN_DATE_ETERNITY = 'dated-block-first-occurence-begindate-eternity',
  DATED_BLOCK_FIRST_OCCURENCE_BEGIN_DATE_ETERNITY_WITH_KEY = 'dated-block-first-occurence-begindate-eternity-withkey',
  DATED_BLOCK_LAST_OCCURENCE_END_DATE_ETERNITY = 'dated-block-last-occurence-enddate-eternity',
  DATED_BLOCK_LAST_OCCURENCE_END_DATE_ETERNITY_WITH_KEY = 'dated-block-last-occurence-enddate-eternity-withkey',
  NON_DATED_BLOCK_BEGIN_DATE_ETERNITY = 'non-dated-block-begindate-eternity',
  NON_DATED_BLOCK_END_DATE_ETERNITY = 'non-dated-block-enddate-eternity',
  WORK_CYCLE_END_DATE_BEFORE_BEGIN_DATE = 'work_cycle_end_date_before_begin_date',

  // nb: BE has just OVERLAPS
  OVERLAPS = 'dated-block-overlaps',
  OVERLAPS_WITH_KEY = 'dated-block-overlaps-withkey',

  // --------
  // ZONES
  ZONE_REQUIRED = 'required',
  VALIDATION_FAILED = 'invalidFormat',

  // nb: in BE it's grouped in VALIDATION_FAILED
  INVALID_EMAIL_ADDRESS = 'invalid_email',
  EMAIL_EXISTS = 'emailExists',
  ZONE_REGEXP = 'invalidPattern',

  // nb: not in BE
  VALUE_NOT_IN_LIST = 'valueNotInList',
  BLOCKING_ERROR_ON_ANOTHER_ELEMENT = 'blocking_error_on_another_element',
  NON_BLOCKING_ERROR_ON_ANOTHER_ELEMENT = 'non_blocking_error_on_another_element'
}

export interface FormGeneratorErrorDetail {
  isBlocking?: boolean;
  status?: boolean;
  additionalValues?: any;
  zone?: string;
}

export interface FormGeneratorError {
  [key: string]: FormGeneratorErrorDetail;
}

export interface ErrorMessage {
  text: string;
  additionalValues?: any;
  zone?: string;
}

export interface BlockingError {
  errorMessage: ErrorMessage;
  tabSheetTransId: string;
  groupZoneTransId: string;
}

export interface CardElementErrors {
  index: number;
  errors: string[];
}

// avoid type error for NativeElementInjectorDirective
export class FormControlWithNativeElement extends UntypedFormControl {
  public nativeElement?: any;
}

export interface AiSourceOrTarget {
  [key: string]: string;
}

export interface PrefillFieldDTO {
  fieldId: string;
  transId: string;
  type: string;
  isRequired: boolean;
}

export interface PrefillGroupDTO {
  groupId: string;
  groupTransId?: string;
  fields: PrefillFieldDTO[];
}

export interface FileMetadataDTO {
  fileId: string;
  fileName: string;
}

export interface PrefillRequestDTO {
  regulationId?: number;
  fileMetadata?: FileMetadataDTO;
  groups: PrefillGroupDTO[];
}

export interface PrefillGroupResponseDTO {
  groupId: string;
  fields: Record<string, PrefillFieldValueDTO>;
}

export type EvidenceLevel = 'STRONG' | 'MEDIUM' | 'WEAK' | 'NONE';

export interface PrefillFieldValueDTO {
  value: string;
  evidenceLevel: EvidenceLevel;
}

export interface PrefillResponseDTO {
  groups: PrefillGroupResponseDTO[];
}

export interface AiGenerateValuesPayload {
  conversationId: string;
  language: string;
  contentGenerationIdentifier: string;
  source: AiSourceOrTarget[];
  target: AiSourceOrTarget;
}

export interface AiGeneratedValues {
  target: AiSourceOrTarget;
}

export interface AiProcessingEvent {
  processing: boolean;
  targetZoneIds?: string[];
}
